import * as THREE from 'three';
import { useCameraTick } from '../../rendering/cameraTick';
import { getActiveEngineCamera } from '../../rendering/engineCameraRegistry';
import { viewBasis } from './quadViewDirections';
import type { QuadDirection } from '../../state/useQuadViewStore';

/**
 * ViewportAxesGizmo — the bottom-right orientation indicator (Rhino-style
 * axis triad). Replaces the origin axes helper as the view-orientation cue:
 * three colored arms (X red / Y green / Z blue, the --color-axis-* tokens)
 * with letter labels, mirroring the current view. The negative direction of
 * each axis is drawn as a recessed dotted arm.
 *
 * Two modes:
 * - `direction`: fixed ortho orientation of a quad cell (static triad).
 * - `live`: follows the engine viewport camera through the camera-change
 *   tick (rendering/cameraTick) — every orbit/zoom/pan re-projects the arms.
 */
const GIZMO_SIZE = 76;
const RING_RADIUS = 34;
const LINE_LENGTH = 21;
const LABEL_DISTANCE = 30;

/** World axes projected per view basis (screen x/y + depth into screen). */
interface AxisArm {
    key: 'x' | 'y' | 'z';
    label: string;
    /** Screen-space direction components (right / up, -1..1). */
    x: number;
    y: number;
    /** Component along the view forward axis (>0 points away). */
    depth: number;
}

const WORLD_AXES = [
    { key: 'x' as const, label: 'X', vector: new THREE.Vector3(1, 0, 0) },
    { key: 'y' as const, label: 'Y', vector: new THREE.Vector3(0, 1, 0) },
    { key: 'z' as const, label: 'Z', vector: new THREE.Vector3(0, 0, 1) },
];

interface ViewBasis {
    right: THREE.Vector3;
    up: THREE.Vector3;
    forward: THREE.Vector3;
}

function projectAxes(basis: ViewBasis): AxisArm[] {
    return WORLD_AXES.map(({ key, label, vector }) => ({
        key,
        label,
        x: vector.dot(basis.right),
        y: vector.dot(basis.up),
        depth: vector.dot(basis.forward),
    }));
}

function basisForCamera(camera: THREE.Camera): ViewBasis {
    // Camera basis columns: right = col 0, up = col 1, the camera looks
    // down its local -Z so forward = -col 2 (matrixWorld is rigid for a
    // camera, so the columns stay orthonormal).
    return {
        right: new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0),
        up: new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1),
        forward: new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 2).negate(),
    };
}

interface ViewportAxesGizmoProps {
    /** Fixed ortho orientation of a quad cell (static triad). */
    direction?: QuadDirection;
    /** Follow the live engine camera instead of a fixed direction. */
    live?: boolean;
    /** Distance from the right edge in px (extra room for a corner ViewCube). */
    rightOffset?: number;
    /** Mode-scoped background token for the disc fill + label halo
        (Settings › Color parity; defaults to the shared viewport token). */
    backgroundToken?: string;
}

export function ViewportAxesGizmo({
    direction,
    live = false,
    rightOffset = 8,
    backgroundToken = '--color-bg-viewport',
}: ViewportAxesGizmoProps) {
    // Re-render on every camera framing change (live mode); static cells
    // ignore it and keep their fixed triad.
    useCameraTick();

    let arms: AxisArm[] | null = null;
    if (direction) {
        arms = projectAxes(viewBasis(direction));
    } else if (live) {
        const camera = getActiveEngineCamera();
        if (camera) arms = projectAxes(basisForCamera(camera));
    }

    if (!arms) return null;

    // Draw farthest arms first so the ones facing the viewer stay on top.
    const ordered = [...arms].sort((a, b) => b.depth - a.depth);

    // Disc fill + label halo follow the owning viewport's mode token
    // (dynamic value → runtime style, the disc reads it in CSS).
    const svgStyle = {
        right: rightOffset,
        '--gizmo-disc-bg': `var(${backgroundToken})`,
    } as React.CSSProperties;

    return (
        <svg
            className="viewport-axes-gizmo"
            style={svgStyle}
            width={GIZMO_SIZE}
            height={GIZMO_SIZE}
            viewBox={`${-GIZMO_SIZE / 2} ${-GIZMO_SIZE / 2} ${GIZMO_SIZE} ${GIZMO_SIZE}`}
            aria-hidden="true"
        >
            <circle r={RING_RADIUS} className="viewport-axes-gizmo__disc" />
            <circle r={RING_RADIUS} className="viewport-axes-gizmo__ring" />
            {ordered.map(({ key, label, x, y }) => (
                <g key={key}>
                    {/* Negative direction: dotted arm on the opposite side. */}
                    <line
                        x1={0}
                        y1={0}
                        x2={-x * LINE_LENGTH}
                        y2={y * LINE_LENGTH}
                        className={`viewport-axes-gizmo__line viewport-axes-gizmo__line--ghost viewport-axes-gizmo__axis-${key}`}
                    />
                    <line
                        x1={0}
                        y1={0}
                        x2={x * LINE_LENGTH}
                        y2={-y * LINE_LENGTH}
                        className={`viewport-axes-gizmo__line viewport-axes-gizmo__axis-${key}`}
                    />
                    <text
                        x={x * LABEL_DISTANCE}
                        y={-y * LABEL_DISTANCE}
                        className={`viewport-axes-gizmo__label viewport-axes-gizmo__axis-${key}`}
                    >
                        {label}
                    </text>
                </g>
            ))}
            {/* Pivot dot covers an axis pointing straight at the camera,
                whose arm collapses to zero length. */}
            <circle r={2} className="viewport-axes-gizmo__ring" />
        </svg>
    );
}
